import clsx from "clsx";

import { formatSessionTitle } from "@/lib/session-title";
import { useStore } from "@/lib/store";
import type { Session, JobStatus } from "@/lib/types";

interface SidebarSessionItemProps {
  session: Session;
}

const statusColors: Record<JobStatus, string> = {
  pending: "bg-cyber-cyan",
  processing: "bg-cyber-magenta animate-neon-pulse",
  awaiting_response: "bg-cyber-magenta animate-neon-pulse",
  completed: "bg-cyber-green",
  cancelled: "bg-cyber-muted",
  failed: "bg-cyber-red",
};

export function SidebarSessionItem({ session }: SidebarSessionItemProps): React.JSX.Element {
  const activeSessionKey = useStore((state) => state.activeSessionKey);
  const showMeta = !useStore((state) => state.filters.mine);
  const theme = useStore((state) => state.theme);
  const selectSession = useStore((state) => state.selectSession);
  const closeSidebar = useStore((state) => state.closeSidebar);

  const isActive = activeSessionKey === session.threadKey;
  const title = formatSessionTitle(session.sessionTitle ?? session.latestMessage.slice(0, 50));
  const itemHoverClass =
    theme === "base" ? "bg-cyber-border/60 hover:bg-cyber-border/80" : "hover:bg-cyber-border/50";
  const itemActiveClass = theme === "base" ? "bg-cyber-border/80" : "bg-cyber-border/50";

  function handleClick(): void {
    selectSession(session.threadKey);
    closeSidebar();
  }

  return (
    <div className="relative z-0 px-1">
      <button
        onClick={handleClick}
        className={clsx(
          "relative z-0 w-full overflow-hidden rounded-xl px-3 py-2 pr-8 text-left transition-all",
          isActive ? itemActiveClass : clsx("bg-transparent", itemHoverClass),
        )}
      >
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              "flex-1 truncate text-[0.8rem] font-normal whitespace-nowrap",
              isActive ? "text-cyber-text" : "text-cyber-text/80 hover:text-cyber-text",
            )}
          >
            {title}
          </span>
          {session.origin !== "interactive" && (
            <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1 py-0.5 text-[9px] tracking-wide text-amber-400 uppercase">
              {session.origin}
            </span>
          )}
          {session.hidden && (
            <span className="border-cyber-border bg-cyber-surface/60 text-cyber-muted rounded border px-1 py-0.5 text-[9px] tracking-wide uppercase">
              Hidden
            </span>
          )}
        </div>
        {session.latestStatus !== "completed" && (
          <span
            className={clsx(
              "absolute top-1/2 right-3 h-1.5 w-1.5 -translate-y-1/2 rounded-full",
              statusColors[session.latestStatus],
            )}
            aria-hidden
          />
        )}
        {showMeta && (
          <div className="text-cyber-muted/70 flex items-center gap-2 text-[10px] whitespace-nowrap">
            {session.userEmail && (
              <>
                <span className="max-w-[80px] truncate">{session.userEmail.split("@")[0]}</span>
                <span>·</span>
              </>
            )}
            <span className="uppercase">{session.source}</span>
          </div>
        )}
      </button>
    </div>
  );
}
